import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DescriptionIcon from '@mui/icons-material/Description';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  applyEdgeChanges,
  applyNodeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { kmeans } from 'ml-kmeans';
import { useProject } from '../api/queries/projects';
import { ProjectBreadcrumb } from '../components/common/ProjectBreadcrumb';
import {
  useBoardById,
  useBoardEmbeddings,
  useAddBoardNoteStandalone,
  useUpdateBoardNoteStandalone,
  useDeleteBoardNoteStandalone,
  useAddBoardConnectionStandalone,
  useDeleteBoardConnectionStandalone,
  type BoardConnectionDto,
  type BoardDto,
  type BoardNoteDto,
} from '../api/queries/boards';

export function BoardDetailRoute() {
  const { boardId } = useParams({ strict: false }) as { boardId: string };
  const navigate = useNavigate();
  const { data: board, isLoading, isError } = useBoardById(boardId);
  const projectId = board?.projectId ?? undefined;
  const { data: project } = useProject(projectId);

  if (isLoading) return <CircularProgress />;
  if (isError || !board)
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">Board not found or failed to load.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate({ to: '/boards' })}>
          Back to boards
        </Button>
      </Paper>
    );

  return (
    <Stack sx={{ height: 'calc(100vh - 100px)' }}>
      {project && projectId && (
        <ProjectBreadcrumb
          projectId={projectId}
          projectName={project.name}
          currentPage="Creativity Board"
        />
      )}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2, mt: 1 }}
      >
        <IconButton onClick={() => navigate({ to: '/boards' })}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {board.name}
        </Typography>
        {board.projectId && (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              navigate({
                to: '/projects/$projectId',
                params: { projectId: board.projectId! },
              })
            }
          >
            Project Dashboard
          </Button>
        )}
      </Stack>

      <StandaloneBoardCanvas boardId={boardId} board={board} />
    </Stack>
  );
}

function StandaloneBoardCanvas({
  boardId,
  board,
}: {
  boardId: string;
  board: BoardDto;
}) {
  const navigate = useNavigate();
  const update = useUpdateBoardNoteStandalone();
  const addNote = useAddBoardNoteStandalone(boardId);
  const addConn = useAddBoardConnectionStandalone(boardId);
  const delNote = useDeleteBoardNoteStandalone(boardId);
  const delConn = useDeleteBoardConnectionStandalone(boardId);
  const fetchEmbeddings = useBoardEmbeddings();
  const [clusterMsg, setClusterMsg] = useState<string | null>(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [addNoteTitle, setAddNoteTitle] = useState('');

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportAnchor);

  const [nodes, setNodes] = useState<Node[]>(() =>
    boardNotesToNodes(board.notes),
  );
  const [edges, setEdges] = useState<Edge[]>(() =>
    boardConnsToEdges(board.connections),
  );

  useEffect(() => {
    setNodes(boardNotesToNodes(board.notes));
    setEdges(boardConnsToEdges(board.connections));
  }, [board.id, board.notes, board.connections]);

  const pending = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const commitPosition = useCallback(
    (id: string, x: number, y: number) => {
      const existing = pending.current.get(id);
      if (existing) clearTimeout(existing);
      const handle = setTimeout(() => {
        update.mutate({ id, x: Math.round(x), y: Math.round(y) });
        pending.current.delete(id);
      }, 300);
      pending.current.set(id, handle);
    },
    [update],
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((prev) => {
        const next = applyNodeChanges(changes, prev);
        for (const change of changes) {
          if (
            change.type === 'position' &&
            change.position &&
            !change.dragging
          ) {
            commitPosition(change.id, change.position.x, change.position.y);
          }
          if (change.type === 'remove') {
            delNote.mutate(change.id);
          }
        }
        return next;
      });
    },
    [commitPosition, delNote],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((prev) => {
        const next = applyEdgeChanges(changes, prev);
        for (const change of changes) {
          if (change.type === 'remove') {
            delConn.mutate(change.id);
          }
        }
        return next;
      });
    },
    [delConn],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;
      addConn.mutate({
        boardId: board.id,
        fromNoteId: conn.source,
        toNoteId: conn.target,
        label: null,
      });
    },
    [addConn, board.id],
  );

  const handleCluster = useCallback(async () => {
    setClusterMsg(null);
    try {
      const result = await fetchEmbeddings.mutateAsync(board.id);
      if (result.disabled) {
        setClusterMsg(
          'Embeddings disabled — enable Google Vertex in config to cluster.',
        );
        return;
      }
      if (result.notes.length < 2) {
        setClusterMsg('Need at least 2 notes with text to cluster.');
        return;
      }
      const vectors = result.notes.map((n) => n.vector);
      const k = Math.max(
        2,
        Math.min(
          result.notes.length,
          Math.round(Math.sqrt(result.notes.length)),
        ),
      );
      const output = kmeans(vectors, k, { seed: 42 });

      const radius = 360;
      const groupRadius = 140;
      const centerX = 500;
      const centerY = 400;
      setNodes((prev) => {
        const moves = new Map<string, { x: number; y: number }>();
        result.notes.forEach((note, i) => {
          const cluster = output.clusters[i];
          const groupAngle = (cluster / k) * Math.PI * 2;
          const groupCx = centerX + radius * Math.cos(groupAngle);
          const groupCy = centerY + radius * Math.sin(groupAngle);
          const indexInCluster = result.notes
            .slice(0, i)
            .filter((_, j) => output.clusters[j] === cluster).length;
          const clusterSize = output.clusters.filter(
            (c) => c === cluster,
          ).length;
          const innerAngle =
            clusterSize > 1 ? (indexInCluster / clusterSize) * Math.PI * 2 : 0;
          const x = groupCx + groupRadius * Math.cos(innerAngle);
          const y = groupCy + groupRadius * Math.sin(innerAngle);
          moves.set(note.noteId, { x: Math.round(x), y: Math.round(y) });
          update.mutate({
            id: note.noteId,
            x: Math.round(x),
            y: Math.round(y),
          });
        });
        return prev.map((n) => {
          const pos = moves.get(n.id);
          return pos ? { ...n, position: pos } : n;
        });
      });
      setClusterMsg(`Clustered ${result.notes.length} notes into ${k} groups.`);
    } catch (ex) {
      setClusterMsg(`Clustering failed: ${(ex as Error).message}`);
    }
  }, [board.id, fetchEmbeddings, update]);

  const handleAddNote = () => {
    setAddNoteTitle('');
    setAddNoteDialogOpen(true);
  };

  const handleAddNoteConfirm = () => {
    if (!addNoteTitle.trim()) return;
    addNote.mutate({
      boardId: board.id,
      kind: 'note',
      content: { title: addNoteTitle.trim(), body: '' },
      x: 200 + Math.random() * 100,
      y: 200 + Math.random() * 100,
    });
    setAddNoteDialogOpen(false);
    setAddNoteTitle('');
  };

  const collectNoteSummaries = useCallback((): string => {
    return board.notes
      .map((n) => {
        const content = n.content as {
          title?: string;
          body?: string;
          name?: string;
        } | null;
        const title = content?.title ?? content?.name ?? '(untitled)';
        const body = content?.body ?? '';
        return body ? `${title}: ${body}` : title;
      })
      .join('\n- ');
  }, [board.notes]);

  const handleGenerateIdeas = useCallback(() => {
    if (board.notes.length === 0) {
      setSnackMessage(
        'Add some notes first so the AI has context to work with.',
      );
      setSnackOpen(true);
      return;
    }
    const noteSummaries = collectNoteSummaries();
    setSnackMessage(
      `AI idea generation seed: ${noteSummaries.slice(0, 100)}...`,
    );
    setSnackOpen(true);
  }, [board.notes.length, collectNoteSummaries]);

  const handleCreativeBrief = useCallback(() => {
    if (board.notes.length === 0) {
      setSnackMessage(
        'Add some notes first so the AI has context to work with.',
      );
      setSnackOpen(true);
      return;
    }
    setSnackMessage(
      'Creative Brief generation — link this board to a book for full AI capabilities.',
    );
    setSnackOpen(true);
  }, [board.notes.length]);

  const handleSuggestConnections = useCallback(() => {
    setSnackMessage(
      'Connection suggestions coming soon — uses embedding similarity to find related notes',
    );
    setSnackOpen(true);
  }, []);

  const handleExportToOutline = useCallback(() => {
    setExportAnchor(null);
    if (!board.projectId) {
      setSnackMessage(
        'Link this board to a book to export notes to Outline or Story Architect',
      );
      setSnackOpen(true);
      return;
    }
    if (board.notes.length === 0) {
      setSnackMessage('No notes to export');
      setSnackOpen(true);
      return;
    }
    // Navigate to the book's board view which has full export capabilities
    navigate({
      to: '/projects/$projectId',
      params: { projectId: board.projectId },
    });
  }, [board, navigate]);

  const handleExportToBeats = useCallback(() => {
    setExportAnchor(null);
    if (!board.projectId) {
      setSnackMessage(
        'Link this board to a book to export notes to Outline or Story Architect',
      );
      setSnackOpen(true);
      return;
    }
    if (board.notes.length === 0) {
      setSnackMessage('No notes to export');
      setSnackOpen(true);
      return;
    }
    // Navigate to the book's board view which has full export capabilities
    navigate({
      to: '/projects/$projectId',
      params: { projectId: board.projectId },
    });
  }, [board, navigate]);

  return (
    <Box
      sx={{
        flex: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        <Button variant="contained" size="small" onClick={handleAddNote}>
          + Note
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCluster}
          disabled={fetchEmbeddings.isPending}
        >
          {fetchEmbeddings.isPending
            ? 'Clustering...'
            : 'Cluster by similarity'}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoFixHighIcon />}
          onClick={handleGenerateIdeas}
        >
          Generate Ideas
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DescriptionIcon />}
          onClick={handleCreativeBrief}
        >
          Creative Brief
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeviceHubIcon />}
          onClick={handleSuggestConnections}
        >
          Suggest Connections
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={(e) => setExportAnchor(e.currentTarget)}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={exportMenuOpen}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={handleExportToOutline}>
            <ListItemIcon>
              <ListAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Export to Outline" />
          </MenuItem>
          <MenuItem onClick={handleExportToBeats}>
            <ListItemIcon>
              <TimelineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Export to Beats" />
          </MenuItem>
        </Menu>

        {clusterMsg && (
          <Alert
            severity="info"
            sx={{ py: 0 }}
            onClose={() => setClusterMsg(null)}
          >
            {clusterMsg}
          </Alert>
        )}
      </Stack>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <MiniMap pannable zoomable />
      </ReactFlow>

      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />

      {/* Add note dialog */}
      <Dialog
        open={addNoteDialogOpen}
        onClose={() => setAddNoteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Note title"
            value={addNoteTitle}
            onChange={(e) => setAddNoteTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddNoteConfirm();
            }}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddNoteConfirm}
            disabled={!addNoteTitle.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function boardNotesToNodes(notes: BoardNoteDto[]): Node[] {
  return notes.map((n) => {
    const content =
      (n.content as { title?: string; body?: string; name?: string } | null) ??
      {};
    const label = content.title ?? content.name ?? '(untitled)';
    const body = content.body;
    return {
      id: n.id,
      position: { x: n.x, y: n.y },
      data: {
        label: (
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {label}
            </Typography>
            {body && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {body}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {n.kind}
            </Typography>
          </Box>
        ),
      },
      style: {
        width: n.width,
        minHeight: n.height,
        background: colorForKind(n.kind),
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: 6,
      },
    };
  });
}

function boardConnsToEdges(conns: BoardConnectionDto[]): Edge[] {
  return conns.map((c) => ({
    id: c.id,
    source: c.fromNoteId,
    target: c.toNoteId,
    label: c.label ?? undefined,
    animated: false,
  }));
}

function colorForKind(kind: string): string {
  switch (kind) {
    case 'entity':
      return '#FFF3E0';
    case 'worldelement':
      return '#E3F2FD';
    default:
      return '#FFFDE7';
  }
}
